import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, CalendarDays, ExternalLink, FileText, Info, Loader2, Ticket } from "lucide-react";
import { api } from "../lib/data";
import { usePlanning } from "../lib/usePlanning";
import { useReglages } from "../lib/useReglages";
import { seatState } from "../lib/planningHelpers";
import { CARTE_10, PARAMETRES, carte10Eligible, texteSemestreCourantCarte10, texteSemestresCarte10, texteValiditeCarte10 } from "../lib/pricing";
import { birthDateError } from "../lib/ages";
import { formatSwissPhone, cityFromNpa, isAutoFilledCity, isValidEmail } from "../lib/formHelpers";
import { ChampNaissance } from "../components/ChampNaissance";
import { EmailSuggestion } from "../components/EmailSuggestion";
import { useFamilleConnectee, coordonneesDe } from "../lib/familleConnectee";
import { useSEO } from "../lib/useSEO";
import { PageDecor } from "../components/decor/PageDecor";

/* Le formulaire « Carte 10 cours », séparé du formulaire semestriel depuis le 08.10.2026
   (décision de l'école) : seulement pour les cours concernés (Réglages → carte_10 de la
   console : Zumba, Reggaeton, hip-hop freestyle…), une carte par cours. La demande entre
   dans la console comme une inscription « Carte 10 cours » : elle compte dans le quota du
   cours et sur sa liste. Elle se paie à l'école, en espèces ou TWINT — taxe d'inscription
   comprise pour un nouvel élève qui ne prend que des cartes. Aucune facture. */

// Les quatre canaux suivis par l'école (console : « D'où viennent les inscriptions »).
const SOURCES_CONNAISSANCE = ["Bouche à oreille", "Instagram", "Google",
  "Événement / spectacle / portes ouvertes", "Autre"];

// Même message que les autres formulaires : la demande est enregistrée AVANT l'envoi des
// e-mails, une erreur réseau ne veut donc pas dire qu'elle est perdue.
function messageErreurEnvoi(e) {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string" && detail && detail.length < 400) return detail;
  return (
    "L'envoi n'a pas pu aboutir correctement. Ta demande a peut-être quand même été " +
    "enregistrée : ne renvoie pas le formulaire tout de suite. Écris-nous à " +
    "info1000arts@gmail.com (ou 079 950 40 31) et nous vérifions immédiatement."
  );
}

const chf = (n) => {
  const v = Number(n) || 0;
  return Number.isInteger(v) ? `CHF ${v}.–` : `CHF ${v.toFixed(2)}`;
};

const jourFr = (iso) => (iso ? String(iso).slice(0, 10).split("-").reverse().join(".") : "");

// Choix en deux boutons (« Pour qui ? », « Première inscription ? », droit à l'image).
const Choix = ({ options, value, onChange, testid }) => (
  <div className="flex flex-wrap gap-3">
    {options.map(([v, label]) => (
      <button key={String(v)} type="button" onClick={() => onChange(v)}
        data-testid={`${testid}-${v === true ? "oui" : v === false ? "non" : v}`}
        aria-pressed={value === v}
        className={`pill ${value === v ? "active" : ""}`}>
        {label}
      </button>
    ))}
  </div>
);

export default function Carte10Form() {
  useSEO({
    title: "Carte 10 cours — inscription 1000 ARTS Cugy VD",
    description: "Inscris-toi avec la carte 10 cours de 1000 ARTS : 10 séances sans engagement semestriel pour la Zumba, le Reggaeton et le hip-hop freestyle, payées à l'école.",
    canonical: "/inscription/carte-10",
  });
  // Prix, nombre de séances, cours concernés et dates de validité : Réglages de la console.
  useReglages();
  const { courses: planningCourses, loading: planningLoading } = usePlanning();
  const [params] = useSearchParams();
  const famille = useFamilleConnectee();

  const [pour, setPour] = useState("enfant");             // « moi » | « enfant »
  const [contact, setContact] = useState({ first_name: "", last_name: "", email: "", phone: "",
    address: "", npa: "", city: "", birth_date: "", source_connaissance: "", source_precision: "" });
  const [eleve, setEleve] = useState({ first_name: "", last_name: "", birth_date: "" });
  const [premiere, setPremiere] = useState(null);          // true | false, à choisir
  const [choisis, setChoisis] = useState([]);              // identifiants des cours
  const [reglement, setReglement] = useState(false);
  const [photo, setPhoto] = useState("oui");
  const [commentaire, setCommentaire] = useState("");
  const [signature, setSignature] = useState("");
  const [signatureOk, setSignatureOk] = useState(false);
  const [etat, setEtat] = useState({ envoi: false, err: "", ok: null });
  const envoiEnCours = useRef(false);

  // Famille connectée à son espace : ses coordonnées sont déjà là.
  useEffect(() => {
    if (!famille) return;
    const c = coordonneesDe(famille);
    setContact((f) => ({
      ...f,
      first_name: f.first_name || c.prenom, last_name: f.last_name || c.nom,
      email: f.email || c.email, phone: f.phone || formatSwissPhone(c.telephone),
      address: f.address || c.adresse, npa: f.npa || c.npa, city: f.city || c.localite,
    }));
    if ((famille.enfants || []).length) setPremiere((p) => (p === null ? false : p));
  }, [famille]);

  // Seuls les cours concernés par la carte, ouverts aux inscriptions : jamais un forfait.
  const cours = useMemo(() => (planningCourses || [])
    .filter((c) => !seatState(c).hidden && !c.forfait && carte10Eligible(c)),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [planningCourses, CARTE_10.disciplines, CARTE_10.mots_cles]);

  // Arrivée depuis le formulaire semestriel ou le planning (?course=<id>) : le cours est coché.
  const preselection = params.get("course") || "";
  useEffect(() => {
    if (!preselection || !cours.some((c) => c.id === preselection)) return;
    setChoisis((v) => (v.includes(preselection) ? v : [...v, preselection]));
  }, [preselection, cours]);

  const basculer = (id) => setChoisis((v) => (v.includes(id) ? v.filter((x) => x !== id) : [...v, id]));
  const choisisCours = cours.filter((c) => choisis.includes(c.id));
  const enAttente = choisisCours.filter((c) => seatState(c).waitlist);
  const avecPlace = choisisCours.filter((c) => !seatState(c).waitlist);
  const taxe = premiere && avecPlace.length ? Number(PARAMETRES.taxe_admin_premiere_inscription) || 0 : 0;
  const aRegler = avecPlace.length * CARTE_10.prix + taxe;
  const semestres = texteSemestresCarte10();
  const echeance = CARTE_10.echeance ? jourFr(CARTE_10.echeance) : "";
  // « (semestre 1 : du 24.08.2026 au 31.01.2027) » : lu dans la console, jamais à retaper.
  const semestreCourant = texteSemestreCourantCarte10();
  const validite = semestreCourant ? ` (${semestreCourant})` : echeance ? ` (jusqu'au ${echeance})` : "";

  const majContact = (k) => (e) => setContact((f) => ({ ...f, [k]: e.target.value }));
  const majEleve = (k) => (e) => setEleve((f) => ({ ...f, [k]: e.target.value }));
  const naissance = pour === "moi" ? contact.birth_date : eleve.birth_date;

  // Ce qui manque : le bouton grisé ne disait pas pourquoi sur les autres formulaires.
  const manquants = [];
  if (!contact.first_name.trim() || !contact.last_name.trim()) {
    manquants.push(pour === "moi" ? "ton prénom et ton nom" : "le prénom et le nom du représentant légal");
  }
  if (!isValidEmail(contact.email)) manquants.push("une adresse e-mail valide");
  if (!contact.phone.trim()) manquants.push("un numéro de téléphone");
  if (!contact.address.trim() || !contact.npa.trim() || !contact.city.trim()) manquants.push("l'adresse complète (rue, NPA, localité)");
  if (pour === "enfant" && (!eleve.first_name.trim() || !eleve.last_name.trim())) manquants.push("le prénom et le nom de l'enfant");
  if (!naissance) manquants.push(pour === "moi" ? "ta date de naissance" : "la date de naissance de l'enfant");
  else if (birthDateError(naissance)) manquants.push("une date de naissance valide");
  if (premiere === null) manquants.push("la réponse à « Première inscription chez 1000 ARTS ? »");
  if (!choisis.length) manquants.push("au moins un cours");
  if (!reglement) manquants.push("accepter le règlement");
  if (signature.trim().length < 2 || !signatureOk) manquants.push("la signature électronique");
  const valide = manquants.length === 0;

  const envoyer = async () => {
    if (envoiEnCours.current || !valide) return;
    envoiEnCours.current = true;
    setEtat({ envoi: true, err: "", ok: null });
    try {
      const participant = pour === "moi"
        ? { first_name: contact.first_name.trim(), last_name: contact.last_name.trim(), birth_date: contact.birth_date }
        : { first_name: eleve.first_name.trim(), last_name: eleve.last_name.trim(), birth_date: eleve.birth_date };
      const { birth_date: _naissance, ...parent } = contact;
      const r = await api.post("/inscriptions/carte10", {
        pour, parent, participant, premiere_inscription: premiere,
        courses: choisisCours.map((c) => ({ id: c.id, name: c.name })),
        accepted: { reglement }, photo_consent: photo, comments: commentaire, signature,
      });
      setEtat({ envoi: false, err: "", ok: r.data });
      window.scrollTo(0, 0);
    } catch (e) {
      setEtat({ envoi: false, ok: null, err: messageErreurEnvoi(e) });
    } finally {
      envoiEnCours.current = false;
    }
  };

  if (etat.ok) {
    const ok = etat.ok;
    const attente = new Set(ok.en_attente || []);
    return (
      <div className="pt-32 pb-24 relative overflow-hidden mesh-bg" data-testid="carte10-confirmation">
        <PageDecor />
        <section className="max-w-2xl mx-auto px-6 md:px-12 relative z-10">
          <div className="w-16 h-16 rounded-full mb-6 flex items-center justify-center" style={{ background: "var(--primary)" }}>
            <Check size={28} color="#fff" />
          </div>
          <div className="eyebrow">Carte {CARTE_10.nb_seances} cours</div>
          <h1 className="font-display text-4xl sm:text-5xl mt-2">C'est noté&nbsp;!</h1>
          <p className="text-base text-[var(--muted)] mt-5 leading-relaxed">
            Merci {contact.first_name.trim()}, ta demande est bien arrivée. Tu reçois aussi ce récapitulatif par e-mail à <strong>{contact.email.trim()}</strong>.
          </p>
          <ul className="mt-6 space-y-2 text-sm">
            {choisisCours.map((c) => (
              <li key={c.id} className="p-3 rounded-xl flex items-start justify-between gap-3" style={{ background: "var(--primary-tint)" }}>
                <span>
                  <strong>{c.name}</strong>
                  <span className="block text-xs text-[var(--muted)]">{c.day} · {c.start}–{c.end} · {c.room}</span>
                </span>
                <span className="font-display whitespace-nowrap" style={attente.has(c.id) ? { color: "#B45309" } : undefined}>
                  {attente.has(c.id) ? "liste d'attente" : chf(ok.prix)}
                </span>
              </li>
            ))}
          </ul>
          <div className="mt-5 p-4 rounded-2xl" style={{ background: "#FFF8E7", border: "1px solid #F5D57A" }} data-testid="carte10-confirmation-montant">
            <div className="text-sm" style={{ color: "#8B6914" }}>
              <strong>À régler à l'école : {chf(ok.a_regler)}</strong>
              {ok.taxe > 0 && ` (dont ${chf(ok.taxe)} de taxe d'inscription)`}, en espèces ou par TWINT, lors de ton premier cours. Aucune facture ne t'est envoyée pour la carte.
              {attente.size > 0 && <span className="block mt-2">Un cours choisi est complet : la carte correspondante est en liste d'attente, rien n'est à payer pour elle tant qu'une place ne s'est pas libérée. Nous t'écrivons dès que c'est le cas.</span>}
            </div>
          </div>
          <p className="text-sm text-[var(--muted)] mt-4 leading-relaxed">
            {CARTE_10.nb_seances} séances par carte, {texteValiditeCarte10()}{validite || (ok.echeance ? ` (jusqu'au ${jourFr(ok.echeance)})` : "")} — les séances non utilisées sont perdues.
          </p>
          <div className="flex flex-wrap gap-3 mt-6">
            <Link to="/planning" className="btn-primary"><CalendarDays size={16} /> Voir le planning</Link>
            <Link to="/inscription" className="btn-ghost">Autre inscription</Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24 relative overflow-hidden mesh-bg" data-testid="page-carte10-form">
      <PageDecor />
      <section className="max-w-3xl mx-auto px-6 md:px-12 relative z-10 space-y-8">
        <Link to="/inscription" className="btn-ghost" data-testid="carte10-retour"><ArrowLeft size={14} /> Retour</Link>
        <div>
          <div className="eyebrow">Inscription</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-2 leading-[0.95]">
            Carte {CARTE_10.nb_seances} cours
          </h1>
          <p className="text-base text-[var(--muted)] mt-5 leading-relaxed">
            Sans engagement semestriel : {CARTE_10.nb_seances} séances du cours choisi, à utiliser quand tu veux, <strong>{texteValiditeCarte10()}</strong>. <strong>Une carte par cours</strong> — {chf(CARTE_10.prix)} la carte, à régler à l'école en espèces ou par TWINT. Pas de facture.
          </p>
          {semestres.length > 0 && (
            <div className="mt-2 text-xs text-[var(--muted)]" data-testid="carte10-semestres">
              {semestres.map((t) => <span key={t} className="block">{t}</span>)}
            </div>
          )}
          <p className="text-sm text-[var(--muted)] mt-3">
            Tu préfères t'engager pour tout le semestre (réductions famille et accumulation comprises) ? <Link to="/inscription?type=semestriel" className="underline font-medium" style={{ color: "var(--primary)" }}>Formulaire « Cours semestriel »</Link>.
          </p>
        </div>

        {famille && (
          <div className="rounded-2xl px-4 py-3 text-sm flex items-start gap-2" data-testid="carte10-connectee"
            style={{ background: "#F0FDF4", border: "1px solid #BBF7D0", color: "#166534" }}>
            <Check size={16} className="shrink-0 mt-0.5" />
            <span>Connecté·e en tant que <strong>{famille.responsable || famille.email}</strong>. Tes coordonnées sont déjà remplies.</span>
          </div>
        )}

        {/* Pour qui ? */}
        <section className="card-flat" data-testid="carte10-pour">
          <div className="font-display text-xl mb-4">Pour qui&nbsp;?</div>
          <Choix testid="carte10-pour" value={pour} onChange={setPour}
            options={[["moi", "Pour moi (adulte)"], ["enfant", "Pour mon enfant"]]} />
        </section>

        {/* Personne de contact */}
        <section className="card-flat">
          <div className="font-display text-xl mb-5">{pour === "moi" ? "Tes coordonnées" : "Représentant légal"}</div>
          <div className="grid md:grid-cols-2 gap-4">
            <div><label className="field-label">Prénom *</label>
              <input data-testid="carte10-prenom" className="input-field" autoComplete="given-name" value={contact.first_name} onChange={majContact("first_name")} /></div>
            <div><label className="field-label">Nom *</label>
              <input data-testid="carte10-nom" className="input-field" autoComplete="family-name" value={contact.last_name} onChange={majContact("last_name")} /></div>
            <div><label className="field-label">E-mail *</label>
              <input data-testid="carte10-email" type="email" autoComplete="email" className="input-field" placeholder="prenom.nom@exemple.ch" value={contact.email} onChange={majContact("email")} />
              <EmailSuggestion value={contact.email} onFix={(v) => setContact((f) => ({ ...f, email: v }))} testid="carte10-email-suggestion" /></div>
            <div><label className="field-label">Téléphone *</label>
              <input data-testid="carte10-telephone" type="tel" inputMode="tel" autoComplete="tel" placeholder="+41 79 950 40 31" className="input-field" value={contact.phone} onChange={(e) => setContact((f) => ({ ...f, phone: formatSwissPhone(e.target.value) }))} /></div>
            <div className="md:col-span-2"><label className="field-label">Adresse *</label>
              <input data-testid="carte10-adresse" className="input-field" autoComplete="street-address" placeholder="Rue et numéro" value={contact.address} onChange={majContact("address")} /></div>
            <div><label className="field-label">NPA *</label>
              <input data-testid="carte10-npa" className="input-field" inputMode="numeric" maxLength={4} placeholder="1053" value={contact.npa}
                onChange={(e) => {
                  const npa = e.target.value.replace(/\D/g, "").slice(0, 4);
                  const suggestion = cityFromNpa(npa);
                  // La localité se remplit seule, sauf si elle a été tapée à la main.
                  setContact((f) => ({ ...f, npa, city: suggestion && isAutoFilledCity(f.city) ? suggestion : f.city }));
                }} /></div>
            <div><label className="field-label">Localité *</label>
              <input data-testid="carte10-localite" className="input-field" placeholder="Cugy VD" value={contact.city} onChange={majContact("city")} /></div>
            {pour === "moi" && (
              <div className="md:col-span-2"><label className="field-label">Date de naissance *</label>
                <ChampNaissance testid="carte10-naissance-moi" value={contact.birth_date} onChange={(v) => setContact((f) => ({ ...f, birth_date: v }))} />
                {birthDateError(contact.birth_date) && <p className="text-xs mt-1 font-medium" style={{ color: "#B91C1C" }}>{birthDateError(contact.birth_date)}</p>}
              </div>
            )}
          </div>
        </section>

        {/* Participant */}
        {pour === "enfant" && (
          <section className="card-flat" data-testid="carte10-enfant">
            <div className="font-display text-xl mb-5">L'enfant</div>
            <div className="grid md:grid-cols-2 gap-4">
              <div><label className="field-label">Prénom *</label>
                <input data-testid="carte10-enfant-prenom" className="input-field" value={eleve.first_name} onChange={majEleve("first_name")} /></div>
              <div><label className="field-label">Nom *</label>
                <input data-testid="carte10-enfant-nom" className="input-field" value={eleve.last_name} onChange={majEleve("last_name")} /></div>
              <div><label className="field-label">Date de naissance *</label>
                <ChampNaissance testid="carte10-enfant-naissance" value={eleve.birth_date} onChange={(v) => setEleve((f) => ({ ...f, birth_date: v }))} />
                {birthDateError(eleve.birth_date) && <p className="text-xs mt-1 font-medium" style={{ color: "#B91C1C" }}>{birthDateError(eleve.birth_date)}</p>}
              </div>
            </div>
          </section>
        )}

        {/* Première inscription : décide de la taxe d'inscription */}
        <section className="card-flat" data-testid="carte10-premiere">
          <div className="font-display text-xl mb-2">Première inscription chez 1000 ARTS&nbsp;? *</div>
          <p className="text-sm text-[var(--muted)] mb-4">
            {`${pour === "moi" ? "Si tu n'as" : "Si ton enfant n'a"} jamais suivi de cours chez nous, une taxe d'inscription unique de ${chf(PARAMETRES.taxe_admin_premiere_inscription)} s'ajoute, à régler avec la carte.`}
          </p>
          <Choix testid="carte10-premiere" value={premiere} onChange={setPremiere}
            options={[[true, "Oui, première fois"], [false, "Non, déjà élève"]]} />
        </section>

        {/* Les cours concernés */}
        <section className="card-flat" data-testid="carte10-cours">
          <div className="font-display text-xl mb-2">Tes cours</div>
          <p className="text-sm text-[var(--muted)] mb-4">
            Seuls les cours sans engagement semestriel se prennent à la carte. Une carte par cours : coche un ou plusieurs cours.
          </p>
          {planningLoading && !cours.length && <div className="text-sm text-[var(--muted)]">Chargement du planning…</div>}
          {!planningLoading && !cours.length && (
            <div className="text-sm text-[var(--muted)]" data-testid="carte10-aucun-cours">
              Aucun cours n'est proposé à la carte pour le moment. Écris-nous à info1000arts@gmail.com.
            </div>
          )}
          <div className="space-y-2">
            {cours.map((c) => {
              const coche = choisis.includes(c.id);
              const s = seatState(c);
              return (
                <label key={c.id} data-testid={`carte10-cours-${c.id}`}
                  className="flex items-start gap-3 p-3 rounded-xl cursor-pointer"
                  style={{ background: coche ? "var(--primary-tint)" : "var(--bg-soft)",
                    border: `1.5px solid ${coche ? "var(--primary)" : "transparent"}` }}>
                  <input type="checkbox" className="mt-1 accent-[var(--primary)] w-4 h-4 shrink-0" checked={coche}
                    onChange={() => basculer(c.id)} data-testid={`carte10-coche-${c.id}`} />
                  <span className="flex-1 min-w-0">
                    <span className="font-medium block">{c.name}</span>
                    <span className="block text-sm text-[var(--muted)]">{c.day} · {c.start}–{c.end} · {c.room}{c.age_label ? ` · ${c.age_label}` : ""}</span>
                    {s.waitlist ? (
                      <span className="block text-xs font-semibold mt-1" style={{ color: "#B45309" }} data-testid={`carte10-complet-${c.id}`}>
                        Complet — la carte sera enregistrée en liste d'attente, rien à payer tant qu'une place ne s'est pas libérée.
                      </span>
                    ) : c.message_site ? (
                      <span className="block text-xs mt-1" style={{ color: s.urgent ? "#B45309" : "var(--muted)" }}>{c.message_site}</span>
                    ) : null}
                  </span>
                  <span className="font-display whitespace-nowrap">{chf(CARTE_10.prix)}</span>
                </label>
              );
            })}
          </div>
        </section>

        {/* Récapitulatif */}
        <section className="card-flat" data-testid="carte10-recap">
          <div className="font-display text-xl mb-4">Récapitulatif</div>
          {choisisCours.length === 0 ? (
            <p className="text-sm text-[var(--muted)]">Aucun cours choisi pour le moment.</p>
          ) : (
            <div className="space-y-2 text-sm">
              {avecPlace.length > 0 && (
                <div className="flex justify-between gap-3">
                  <span>{avecPlace.length} carte{avecPlace.length > 1 ? "s" : ""} × {chf(CARTE_10.prix)}</span>
                  <span className="whitespace-nowrap">{chf(avecPlace.length * CARTE_10.prix)}</span>
                </div>
              )}
              {taxe > 0 && (
                <div className="flex justify-between gap-3" data-testid="carte10-recap-taxe">
                  <span>Taxe d'inscription (première inscription, une seule fois)</span>
                  <span className="whitespace-nowrap">+ {chf(taxe)}</span>
                </div>
              )}
              {enAttente.length > 0 && (
                <div className="flex justify-between gap-3" style={{ color: "#B45309" }}>
                  <span>{enAttente.length} carte{enAttente.length > 1 ? "s" : ""} en liste d'attente</span>
                  <span className="whitespace-nowrap">à payer plus tard</span>
                </div>
              )}
              <div className="pt-3 mt-2 border-t border-[var(--border-soft)] flex justify-between items-baseline gap-3">
                <span className="font-display text-lg">À régler à l'école</span>
                <span className="font-display text-2xl sm:text-3xl whitespace-nowrap" style={{ color: "var(--primary)" }} data-testid="carte10-total">{chf(aRegler)}</span>
              </div>
            </div>
          )}
          <div className="mt-4 flex items-start gap-3 p-4 rounded-2xl" style={{ background: "#FFF8E7", border: "1px solid #F5D57A" }}>
            <Info size={16} className="mt-0.5 shrink-0" style={{ color: "#B7791F" }} />
            <div className="text-xs leading-relaxed" style={{ color: "#8B6914" }}>
              <strong>À régler à l'école en espèces ou par TWINT</strong>, lors de ton premier cours — aucune facture n'est envoyée.
              {" "}{CARTE_10.nb_seances} séances par carte, {texteValiditeCarte10()}{validite} : les séances non utilisées sont perdues. La carte est nominative et n'ouvre pas droit aux réductions famille et accumulation.
              {premiere && <> Tu suis aussi un cours au semestre&nbsp;? La taxe d'inscription figure alors sur la facture semestrielle et n'est pas demandée avec la carte.</>}
            </div>
          </div>
        </section>

        {/* Comment as-tu connu 1000 ARTS ? (facultatif) + commentaire */}
        <section className="card-flat">
          <label className="field-label">Comment as-tu connu 1000 ARTS ? <span className="text-[var(--muted)] font-normal">(facultatif)</span></label>
          <select data-testid="carte10-source" className="input-field mt-1" value={contact.source_connaissance} onChange={majContact("source_connaissance")}>
            <option value="">Je préfère ne pas répondre</option>
            {SOURCES_CONNAISSANCE.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          {contact.source_connaissance === "Autre" && (
            <input data-testid="carte10-source-precision" className="input-field mt-3" placeholder="Dis-nous comment (affiche, passage devant l'école, école, club…)"
              value={contact.source_precision} onChange={majContact("source_precision")} />
          )}
          <label className="field-label mt-5 block">Un commentaire pour l'école <span className="text-[var(--muted)] font-normal">(facultatif)</span></label>
          <textarea data-testid="carte10-commentaire" rows={3} className="input-field" value={commentaire} onChange={(e) => setCommentaire(e.target.value)} />
        </section>

        {/* Règlement, droit à l'image, signature */}
        <section className="card-flat">
          <div className="font-display text-xl mb-4">Conditions</div>
          <a href="/reglement" target="_blank" rel="noreferrer" data-testid="carte10-reglement-lien"
            className="inline-flex items-center gap-1 text-sm font-medium" style={{ color: "var(--primary)" }}>
            <FileText size={14} /> Lire le règlement général <ExternalLink size={12} />
          </a>
          <label className="flex items-start gap-3 text-sm cursor-pointer mt-4">
            <input type="checkbox" data-testid="carte10-reglement" checked={reglement} onChange={(e) => setReglement(e.target.checked)} className="mt-1 accent-[var(--primary)]" />
            <span>J'ai lu et j'accepte le règlement général de 1000ARTS.</span>
          </label>

          <div className="mt-6 pt-5 border-t border-[var(--border-soft)]">
            <div className="font-medium mb-2">Droit à l'image</div>
            <p className="text-sm text-[var(--muted)] mb-3">
              J'autorise l'utilisation de photos / vidéos à des fins de communication. Ce choix est facultatif et n'a aucune conséquence sur l'inscription.
            </p>
            <Choix testid="carte10-photo" value={photo} onChange={setPhoto} options={[["oui", "Oui"], ["non", "Non"]]} />
          </div>

          <div className="mt-6 pt-5 border-t border-[var(--border-soft)]">
            <label className="field-label">Signature électronique — nom complet *</label>
            <input data-testid="carte10-signature" className="input-field" placeholder="Ex : Marie Dupont" value={signature} onChange={(e) => setSignature(e.target.value)} />
            <label className="flex items-start gap-3 text-sm cursor-pointer mt-3">
              <input type="checkbox" data-testid="carte10-signature-ok" checked={signatureOk} onChange={(e) => setSignatureOk(e.target.checked)} className="mt-1 accent-[var(--primary)]" />
              <span>Je confirme que ce nom fait office de signature électronique pour cette inscription.</span>
            </label>
          </div>
        </section>

        {etat.err && <div className="text-sm" style={{ color: "#B91C1C" }} data-testid="carte10-erreur">{etat.err}</div>}

        {!valide && (
          <div className="p-4 rounded-2xl text-sm" data-testid="carte10-manquants"
            style={{ background: "#FEF3C7", border: "1px solid #FCD34D", color: "#92400E" }}>
            <div className="font-semibold mb-2">Pour envoyer ta demande, il te reste à&nbsp;:</div>
            <ul className="space-y-1 list-disc pl-5">
              {manquants.map((m) => <li key={m}>{m}</li>)}
            </ul>
          </div>
        )}

        <button className="btn-primary w-full justify-center" disabled={!valide || etat.envoi}
          data-testid="carte10-envoyer" onClick={envoyer}>
          {etat.envoi ? <><Loader2 size={16} className="animate-spin" /> Envoi…</>
            : <><Ticket size={16} /> Envoyer ma demande de carte <ArrowRight size={16} /></>}
        </button>
      </section>
    </div>
  );
}
